import { Dependencies } from "@planzone/core";
import { Document, Folder, Identifiable } from "@planzone/dto";
import mimeDb from "mime-db";
import JSZip from "jszip";
import { AxiosInstance } from "axios";

function utf8ToB64(data: string) {
  return btoa(data);
}

function mimeTypeToExtension(mimeType: string): string {
  const mimeMap = mimeDb as Record<string, { extensions: string[] }>;

  for (const mime in mimeMap) {
    if (
      mimeMap[mime].extensions &&
      mimeMap[mime].extensions.length > 0 &&
      mime === mimeType
    ) {
      return mimeMap[mime].extensions[0];
    }
  }

  return "";
}

function prepareDownload(folder: Identifiable<Folder>, zip: JSZip) {
  const prepareDocument = (doc: Identifiable<Document>) => {
    const ext = mimeTypeToExtension(doc.mimeType);

    zip.file(
      ext ? `${doc.name}.${ext}` : doc.name,
      fetch(doc.uri).then((r) => r.blob()),
    );
  };
  const prepareFolder = (f: Identifiable<Folder>) => {
    prepareDownload(f, zip.folder(f.name) || zip);
  };

  folder.children.forEach((child) =>
    child.type === "document" ? prepareDocument(child) : prepareFolder(child),
  );
}

function downloadFolder(folder: Identifiable<Folder>) {
  const zip = new JSZip();

  prepareDownload(folder, zip);

  zip.generateAsync({ type: "blob" }).then((blob) => {
    const downloadLink = document.createElement("a");
    downloadLink.href = URL.createObjectURL(blob);
    downloadLink.download = `${folder.name}.zip`;
    downloadLink.click();
  });
}

function downloadDocument({ uri, name }: Identifiable<Document>) {
  const downloadLink = document.createElement("a");
  downloadLink.href = uri;
  downloadLink.download = name;
  downloadLink.style.display = "none";

  document.body.appendChild(downloadLink);
  downloadLink.click();
  document.body.removeChild(downloadLink);
}

function createFileSystem(
  session: Dependencies["session"],
  axios: AxiosInstance,
): Dependencies["fileSystem"] {
  return {
    write(name, data) {
      const element = document.createElement("a");

      element.href = data;
      element.download = name;
      element.click();

      return Promise.resolve({
        name,
        uri: utf8ToB64(data),
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        size: data.length,
      });
    },
    writeFolderToDisk(folder) {
      downloadFolder(folder);

      return Promise.resolve();
    },
    writeDocumentToDisk(document) {
      downloadDocument(document);

      return Promise.resolve();
    },
  };
}

export { createFileSystem };
